/** The paper a poster is laid out for. */
export type PosterSize = 'A4' | 'A5';

/** Everything printed on a poster, already translated. */
export interface PosterContent {
  size: PosterSize;
  language: string;
  businessName: string;
  businessLogo: string | null;
  poweredBy: string;
  /** The department the code is for, or null for the business-wide code. */
  departmentName: string | null;
  /** The QR code as a PNG data URL. */
  qrCodeDataUrl: string;
  /** The URL the code encodes, printed under it for anyone whose camera cannot read it. */
  qrCodeUrl: string;
  heading: string;
  lead: string;
  steps: [string, string, string];
  notWhistleblowing: string;
  /** "Code valid until …", or null for a code that does not expire. */
  validUntil: string | null;
  emergency: string;
}

/** Escapes text for HTML: business and department names come from the database. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The poster as a self-contained HTML document sized for its paper. A5 keeps the A4 layout at about
 * 70 %, which is what the paper is, so both print from the same markup.
 */
export function buildPosterHtml(content: PosterContent): string {
  const scale = content.size === 'A4' ? 1 : 0.707;
  const px = (value: number) => `${Math.round(value * scale)}px`;
  const department = content.departmentName
    ? `<p class="department">${escapeHtml(content.departmentName)}</p>`
    : '';
  const validUntil = content.validUntil ? `<span>${escapeHtml(content.validUntil)}</span>` : '<span></span>';
  const steps = content.steps
    .map((step, index) => `<li><span class="number">${index + 1}</span><span>${escapeHtml(step)}</span></li>`)
    .join('');

  return `<!doctype html>
<html lang="${escapeHtml(content.language)}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(content.heading)}</title>
<style>
  @page { size: ${content.size} portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; background: #ffffff; color: #131a24; }
  body { font-family: 'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif; }
  .page { width: ${content.size === 'A4' ? '210mm' : '148mm'}; height: ${content.size === 'A4' ? '297mm' : '210mm'};
    padding: ${px(60)} ${px(66)} ${px(52)}; display: flex; flex-direction: column; gap: ${px(32)}; overflow: hidden; }
  header { padding-bottom: ${px(20)}; border-bottom: 2px solid #131a24; }
  .business { margin: 0; font-size: ${px(26)}; font-weight: 750; }
  .business-logo { display: block; max-width: 100%; width: auto; height: ${px(60)}; object-fit: contain; object-position: left; }
  .department { margin: ${px(4)} 0 0; font-size: ${px(19)}; color: #3f4a58; }
  h1 { margin: 0; font-size: ${px(54)}; line-height: 1.05; font-weight: 800; letter-spacing: -0.02em; }
  .lead { margin: ${px(12)} 0 0; font-size: ${px(22)}; line-height: 1.45; color: #3f4a58; }
  .code { display: flex; gap: ${px(36)}; align-items: center; }
  .code img { width: ${px(300)}; height: ${px(300)}; flex: none; image-rendering: pixelated; }
  ol { margin: 0; padding: 0; list-style: none; display: grid; gap: ${px(22)}; font-size: ${px(20)}; line-height: 1.4; }
  li { display: grid; grid-template-columns: ${px(42)} 1fr; gap: ${px(14)}; align-items: start; }
  .number { display: flex; align-items: center; justify-content: center; width: ${px(42)}; height: ${px(42)};
    border: 2px solid #131a24; border-radius: 50%; font-weight: 700; }
  .number + span { padding-top: ${px(7)}; }
  .url { margin: 0; font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; font-size: ${px(14)}; color: #3f4a58;
    word-break: break-all; }
  .bottom { margin-top: auto; display: grid; gap: ${px(14)}; }
  .note { margin: 0; padding: ${px(16)} ${px(18)}; border: 1px solid #a9b3c1; border-radius: 12px; font-size: ${px(16)};
    line-height: 1.5; color: #3f4a58; }
  .meta { display: flex; justify-content: space-between; gap: ${px(16)}; font-size: ${px(14)}; color: #5a6472; }
  .powered-by { margin: 0; text-align: center; font-size: ${px(14)}; color: #5a6472; }
</style>
</head>
<body>
<div class="page">
  <header>
    ${content.businessLogo
      ? `<img class="business-logo" src="${escapeHtml(content.businessLogo)}" alt="${escapeHtml(content.businessName)}"><p class="business" hidden>${escapeHtml(content.businessName)}</p>`
      : `<p class="business">${escapeHtml(content.businessName)}</p>`}
    ${department}
  </header>
  <div>
    <h1>${escapeHtml(content.heading)}</h1>
    <p class="lead">${escapeHtml(content.lead)}</p>
  </div>
  <div class="code">
    <img src="${content.qrCodeDataUrl}" alt="">
    <ol>${steps}</ol>
  </div>
  <p class="url">${escapeHtml(content.qrCodeUrl)}</p>
  <div class="bottom">
    <p class="note">${escapeHtml(content.notWhistleblowing)}</p>
    <div class="meta">${validUntil}<span>${escapeHtml(content.emergency)}</span></div>
    <p class="powered-by">${escapeHtml(content.poweredBy)}</p>
  </div>
</div>
</body>
</html>`;
}

/**
 * Opens the poster in a new window and starts printing once its images have settled, so the dialog
 * offers "Save as PDF" as well as a printer. Returns false when the browser blocked the window.
 */
export function printPoster(content: PosterContent): boolean {
  const posterWindow = window.open('', '_blank');
  if (!posterWindow) {
    return false;
  }

  posterWindow.document.open();
  posterWindow.document.write(buildPosterHtml(content));
  posterWindow.document.close();

  const images = Array.from(posterWindow.document.images);
  const loaded = images.map(image => new Promise<void>(resolve => {
    const settle = () => {
      if (image.classList.contains('business-logo') && image.naturalWidth === 0) {
        image.hidden = true;
        image.style.display = 'none';
        posterWindow.document.querySelector<HTMLElement>('.business').hidden = false;
      }
      resolve();
    };

    if (image.complete) {
      settle();
    } else {
      image.addEventListener('load', settle, { once: true });
      image.addEventListener('error', settle, { once: true });
    }
  }));

  void Promise.all(loaded).then(() => {
    if (posterWindow.closed) {
      return;
    }
    posterWindow.focus();
    posterWindow.print();
  });

  return true;
}
